// Glossary Page (Client-side filter)
// EN/DE localized term list with simple in-page search.

'use client';

import React, { useMemo, useState } from 'react';
import { useMessages, useTranslations } from 'next-intl';
import { generateBreadcrumbJsonLd } from '@/lib/seo/breadcrumb-json-ld';
import { getSeoCanonicalBaseUrl } from '@/lib/urls/canonical';
import { Input } from '@/components/ui/input';

type Term = { id: string; term: string; definition: string; example?: string };
const OPTIONAL_TERM_IDS = ['blastRadius', 'stickyContext', 'trustLevel'] as const;

export default function GlossaryPage() {
  const t = useTranslations('docs.glossary');
  const baseUrl = getSeoCanonicalBaseUrl();
  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: 'Home', url: baseUrl },
    { name: 'Documentation', url: `${baseUrl}/docs` },
    { name: 'Glossary', url: `${baseUrl}/docs/glossary` },
  ]);
  const messages = useMessages() as {
    docs?: {
      glossary?: {
        terms?: Record<string, { term?: string; def?: string }>;
      };
    };
  };
  const [query, setQuery] = useState('');

  const terms: Term[] = useMemo(
    () => {
      const termMessages = messages.docs?.glossary?.terms ?? {};
      const baseTerms: Term[] = [
        { id: 'calibrationCertificate', term: t('terms.calibrationCertificate.term'), definition: t('terms.calibrationCertificate.def') },
        { id: 'instrument', term: t('terms.instrument.term'), definition: t('terms.instrument.def') },
        { id: 'assetId', term: t('terms.assetId.term'), definition: t('terms.assetId.def') },
        { id: 'traceability', term: t('terms.traceability.term'), definition: t('terms.traceability.def') },
        { id: 'accreditation', term: t('terms.accreditation.term'), definition: t('terms.accreditation.def') },
        { id: 'expiry', term: t('terms.expiry.term'), definition: t('terms.expiry.def') },
        { id: 'uncertainty', term: t('terms.uncertainty.term'), definition: t('terms.uncertainty.def') },
        { id: 'auditPack', term: t('terms.auditPack.term'), definition: t('terms.auditPack.def') },
        { id: 'version', term: t('terms.version.term'), definition: t('terms.version.def') },
      ];

      OPTIONAL_TERM_IDS.forEach((id) => {
        const optionalTerm = termMessages[id];
        if (optionalTerm?.term && optionalTerm?.def) {
          baseTerms.push({
            id,
            term: optionalTerm.term,
            definition: optionalTerm.def,
          });
        }
      });

      return baseTerms;
    },
    [messages, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terms;
    return terms.filter((x) => x.term.toLowerCase().includes(q) || x.definition.toLowerCase().includes(q));
  }, [query, terms]);

  return (
    <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
    />
    <div className="min-h-screen bg-background">
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <header className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">{t('title')}</h1>
          <p className="mt-2 text-muted-foreground">{t('subtitle')}</p>
        </header>

        <div className="mb-6">
          <label htmlFor="glossary-search" className="sr-only">
            {t('search.label')}
          </label>
          <Input
            id="glossary-search"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <dl className="space-y-6">
          {filtered.map((item) => (
            <div key={item.id} id={item.id}>
              <dt className="text-lg font-semibold">{item.term}</dt>
              <dd className="mt-1 text-sm text-muted-foreground">{item.definition}</dd>
            </div>
          ))}
        </dl>
      </main>
    </div>
    </>
  );
}
